import React from 'react';
import styled from 'styled-components';
import {Alert, Button, Col, Divider, Empty, Image, Row, Spin, Tag, Typography} from 'antd';
import {MedicineBoxOutlined, UserAddOutlined} from '@ant-design/icons';
import {useNavigate, useParams} from 'react-router-dom';
import SubBanner from '../components/SubBanner';
import Section from '../components/Section';
import ContentContainer from '../components/ContentContainer';
import Title from '../components/Title';
import useDefaultRequest from '../hooks/useDefaultRequest';
import ENDPOINTS from '../constants/endpoints';
import {formatPath, ROUTES} from '../constants/PathRoutes';
import {GRAY_3, GREE_1} from '../utils/colors';
import {moneyMask} from '../utils/utils';
import imagePlaceholder from '../assets/imgs/image-placeholder.jpg';

const {Paragraph} = Typography;

const StyledSection = styled(Section)`
    padding-top: 24px;
`;

const Price = styled.span`
    font-size: 36px;
    font-weight: bold;
    color: ${GREE_1};
`;

const StyledTitle = styled(Title)`
    margin-top: 0;
    margin-bottom: 0 !important;
    color: ${GRAY_3} !important;
`;

const StyledTitleDescription = styled(Title)`
    white-space: nowrap;
    width: min-content;

    &:after {
        content: '';
        margin-top: 8px;
        width: 75px;
        height: 2px;
        display: block;
        background: ${GREE_1};
    }
`;

const StyledParagraph = styled(Paragraph)`
    color: #75717a;
    font-weight: 500;
    line-height: 30px;
    font-size: 18px;
`;

const StyledImage = styled(Image)`
    object-fit: cover;
    object-position: center;
`;

const CategoryTag = styled(Tag)`
    font-size: 13px;
    padding: 4px 12px;
    border-radius: 12px;
`;

export default function PublicProductDetail() {
    const {slug} = useParams();
    const navigate = useNavigate();

    const {data: product, isLoading} = useDefaultRequest({
        url: formatPath(ENDPOINTS.ECOMMERCE_PRODUCT_PUBLIC_DETAIL, {slug}),
        dataDefault: null,
        dependence: [slug],
    });

    if (isLoading) {
        return (
            <>
                <SubBanner title="Buscando produto..."/>
                <StyledSection>
                    <ContentContainer>
                        <Row justify="center" style={{padding: '60px 0'}}>
                            <Spin size="large"/>
                        </Row>
                    </ContentContainer>
                </StyledSection>
            </>
        );
    }

    if (!product) {
        return (
            <>
                <SubBanner title="Produto não encontrado"/>
                <StyledSection>
                    <ContentContainer>
                        <Empty
                            description="Produto não encontrado"
                            style={{padding: '60px 0'}}
                        />
                    </ContentContainer>
                </StyledSection>
            </>
        );
    }

    return (
        <>
            <SubBanner title={product.name}/>
            <StyledSection>
                <ContentContainer>
                    <Row gutter={[24, 24]}>
                        {/* Imagens */}
                        <Col xs={24} md={12}>
                            <Image.PreviewGroup>
                                <Row gutter={[0, 16]}>
                                    <Col xs={24}>
                                        <StyledImage
                                            height={480}
                                            width="100%"
                                            style={{objectFit: 'cover'}}
                                            src={product.main_picture || product.image || imagePlaceholder}
                                            fallback={imagePlaceholder}
                                        />
                                    </Col>
                                    {(product.first_picture || product.second_picture) && (
                                        <Row gutter={[16, 16]} style={{width: '100%', margin: 0}}>
                                            {product.first_picture && (
                                                <Col xs={12}>
                                                    <StyledImage
                                                        height={200}
                                                        width="100%"
                                                        style={{objectFit: 'cover'}}
                                                        src={product.first_picture}
                                                        fallback={imagePlaceholder}
                                                    />
                                                </Col>
                                            )}
                                            {product.second_picture && (
                                                <Col xs={12}>
                                                    <StyledImage
                                                        height={200}
                                                        width="100%"
                                                        style={{objectFit: 'cover'}}
                                                        src={product.second_picture}
                                                        fallback={imagePlaceholder}
                                                    />
                                                </Col>
                                            )}
                                        </Row>
                                    )}
                                </Row>
                            </Image.PreviewGroup>
                        </Col>

                        {/* Informações */}
                        <Col xs={24} md={12}>
                            <Row gutter={[0, 16]}>
                                {product.category_name && (
                                    <Col xs={24}>
                                        <CategoryTag color="green">{product.category_name}</CategoryTag>
                                    </Col>
                                )}
                                <Col xs={24}>
                                    <StyledTitle>{product.name}</StyledTitle>
                                </Col>
                                <Col xs={24}>
                                    <Price>R$ {moneyMask(product.price)}</Price>
                                </Col>
                                {product.resume && (
                                    <Col xs={24}>
                                        <StyledParagraph>{product.resume}</StyledParagraph>
                                    </Col>
                                )}
                                {product.requires_prescription && (
                                    <Col xs={24}>
                                        <Alert
                                            message="Prescrição médica necessária"
                                            description="Este produto requer uma prescrição médica válida para compra. Acesse a área do associado para adquiri-lo."
                                            type="warning"
                                            showIcon
                                            icon={<MedicineBoxOutlined/>}
                                        />
                                    </Col>
                                )}
                                <Col xs={24}>
                                    <Button
                                        size="large"
                                        icon={<UserAddOutlined/>}
                                        onClick={() => navigate(ROUTES.AREA_ASSOCIADO_CADASTRO)}
                                        style={{
                                            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                                            border: 'none',
                                            color: '#000',
                                            fontWeight: 'bold',
                                            borderRadius: 20,
                                        }}
                                    >
                                        Seja um Associado
                                    </Button>
                                </Col>
                            </Row>
                        </Col>

                        {/* Descrição */}
                        {product.description && (
                            <Col xs={24}>
                                <Divider/>
                                <StyledTitleDescription>Descrição</StyledTitleDescription>
                                <div
                                    style={{marginTop: 16}}
                                    dangerouslySetInnerHTML={{__html: product.description}}
                                />
                            </Col>
                        )}
                    </Row>
                </ContentContainer>
            </StyledSection>
        </>
    );
}
